import React, { useEffect, useState } from 'react';
import { BUILT_IN_CATALOGS } from '../../i18n/translate';
import {
  i18nErrorMessage,
  renameLanguage,
  setLanguageAdmins,
  upsertLanguage,
  type AdminLanguage,
} from '../../services/i18nService';
import { useDialog } from '../../context/DialogContext';
import styles from '../../styles/translationAdmin.module.css';

interface AddLanguageProps {
  existingCodes: string[];
  onCreated: (lang: AdminLanguage) => void;
}

/** Translation managers only: add a language (starts under construction, invisible to members). */
export const AddLanguageForm: React.FC<AddLanguageProps> = ({ existingCodes, onCreated }) => {
  const suggestSpanish = !existingCodes.includes('es');
  const [code, setCode] = useState(suggestSpanish ? 'es' : '');
  const [name, setName] = useState(suggestSpanish ? 'Spanish' : '');
  const [nativeName, setNativeName] = useState(suggestSpanish ? 'Español' : '');
  const [fallback, setFallback] = useState('en');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (existingCodes.includes(trimmed)) {
      setError(`${trimmed} already exists.`);
      return;
    }
    setSaving(true);
    try {
      const fallbacks = fallback && fallback !== 'en' ? [fallback, 'en'] : ['en'];
      const lang = await upsertLanguage(trimmed, {
        name,
        native_name: nativeName,
        fallbacks,
        tier: trimmed.includes('-') ? 2 : 1,
      });
      onCreated(lang);
      setCode('');
      setName('');
      setNativeName('');
      setFallback('en');
    } catch (err) {
      setError(i18nErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>Add a language</h2>
      <p className={styles.muted}>
        New languages start under construction: hidden from members, previewable by you and the
        language admins you assign.
      </p>
      <div className={styles.formGrid}>
        <label>
          Code
          <input className={styles.input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="es or es-MX" />
        </label>
        <label>
          English name
          <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Spanish" />
        </label>
        <label>
          Native name
          <input className={styles.input} value={nativeName} onChange={(e) => setNativeName(e.target.value)} placeholder="Español" />
        </label>
        <label>
          Falls back to
          <select className={styles.input} value={fallback} onChange={(e) => setFallback(e.target.value)}>
            <option value="en">English (en)</option>
            {existingCodes.map((c) => (
              <option key={c} value={c}>
                {c} → English
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <div className={styles.error}>{error}</div>}
      <button type="submit" className={styles.button} disabled={saving || !code.trim() || !name.trim() || !nativeName.trim()}>
        {saving ? 'Adding…' : 'Add language'}
      </button>
    </form>
  );
};

interface LanguageDetailsProps {
  language: AdminLanguage;
  allLanguages: AdminLanguage[];
  onSaved: (oldCode: string, lang: AdminLanguage) => void;
}

/** Translation managers only: edit code, names, and fallback. */
const LanguageDetails: React.FC<LanguageDetailsProps> = ({ language, allLanguages, onSaved }) => {
  const currentFallback = (language.fallbacks || ['en'])[0] || 'en';
  const [code, setCode] = useState(language.code);
  const [name, setName] = useState(language.name);
  const [nativeName, setNativeName] = useState(language.native_name);
  const [fallback, setFallback] = useState(currentFallback);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { confirmAsync } = useDialog();

  useEffect(() => {
    setCode(language.code);
    setName(language.name);
    setNativeName(language.native_name);
    setFallback((language.fallbacks || ['en'])[0] || 'en');
  }, [language.code, language.name, language.native_name, language.fallbacks]);

  const isPublished = language.status === 'published';
  const newCode = code.trim();
  const codeChanged = newCode !== language.code;
  const dirty =
    codeChanged || name.trim() !== language.name || nativeName.trim() !== language.native_name || fallback !== currentFallback;
  const fallbackOptions = allLanguages.filter((l) => l.code !== language.code);

  const save = async () => {
    setError(null);
    setSaved(false);
    if (codeChanged) {
      if (allLanguages.some((l) => l.code === newCode)) {
        setError(`${newCode} already exists.`);
        return;
      }
      const lostDrafts = BUILT_IN_CATALOGS[language.code] && !BUILT_IN_CATALOGS[newCode];
      const message =
        'Saved translations, documents, and language admins move to the new code.' +
        (lostDrafts
          ? ` The pre-loaded AI drafts are tied to the code ${language.code}, so ${newCode} won't have them — strings nobody has saved will show as Missing.`
          : '');
      const ok = await confirmAsync({
        title: `Change the code from ${language.code} to ${newCode}?`,
        message,
        confirmText: 'Change code',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      let targetCode = language.code;
      if (codeChanged) targetCode = (await renameLanguage(language.code, newCode)).code;
      const fallbacks = fallback && fallback !== 'en' && fallback !== targetCode ? [fallback, 'en'] : ['en'];
      const updated = await upsertLanguage(targetCode, {
        name,
        native_name: nativeName,
        fallbacks,
        tier: targetCode.includes('-') ? 2 : 1,
      });
      onSaved(language.code, updated);
      setSaved(true);
    } catch (err) {
      setError(i18nErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h4 className={styles.subheading}>Details</h4>
      <div className={styles.formGrid}>
        <label>
          Code
          <input
            className={styles.input}
            value={code}
            disabled={isPublished}
            onChange={(e) => {
              setCode(e.target.value);
              setSaved(false);
            }}
          />
          {isPublished && <span className={styles.muted}>Unpublish to change the code.</span>}
        </label>
        <label>
          English name
          <input
            className={styles.input}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
          />
        </label>
        <label>
          Native name
          <input
            className={styles.input}
            value={nativeName}
            onChange={(e) => {
              setNativeName(e.target.value);
              setSaved(false);
            }}
          />
        </label>
        <label>
          Falls back to
          <select
            className={styles.input}
            value={fallback}
            onChange={(e) => {
              setFallback(e.target.value);
              setSaved(false);
            }}
          >
            <option value="en">English (en)</option>
            {fallbackOptions.map((l) => (
              <option key={l.code} value={l.code}>
                {l.native_name || l.name} ({l.code}), then English
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          onClick={() => void save()}
          disabled={saving || !dirty || !newCode || !name.trim() || !nativeName.trim()}
        >
          {saving ? 'Saving…' : 'Save details'}
        </button>
        {saved && !dirty && <span className={styles.muted}>Saved.</span>}
      </div>
    </div>
  );
};

interface LanguageSettingsCardProps {
  language: AdminLanguage;
  allLanguages: AdminLanguage[];
  onDetailsSaved: (oldCode: string, lang: AdminLanguage) => void;
  onAdminsSaved: (admins: string[]) => void;
}

/** Translation managers only: one card per language with its details and language admins. */
export const LanguageSettingsCard: React.FC<LanguageSettingsCardProps> = ({
  language,
  allLanguages,
  onDetailsSaved,
  onAdminsSaved,
}) => (
  <div className={styles.card}>
    <h3>
      {language.native_name || language.name} <span className={styles.muted}>({language.code})</span>
    </h3>
    <LanguageDetails language={language} allLanguages={allLanguages} onSaved={onDetailsSaved} />
    <LanguageAdmins language={language} onSaved={onAdminsSaved} />
  </div>
);

interface LanguageAdminsProps {
  language: AdminLanguage;
  onSaved: (admins: string[]) => void;
}

/** Translation managers only: assign the language admins who review and approve. */
const LanguageAdmins: React.FC<LanguageAdminsProps> = ({ language, onSaved }) => {
  const [draft, setDraft] = useState((language.admins || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft((language.admins || []).join('\n'));
  }, [language.code, language.admins]);

  const save = async () => {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const emails = draft
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const admins = await setLanguageAdmins(language.code, emails);
      onSaved(admins);
      setSaved(true);
    } catch (err) {
      setError(i18nErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.settingsSection}>
      <h4 className={styles.subheading}>Language admins</h4>
      <p className={styles.muted}>
        One email per line. Each person needs a Recovery Commons account. Language admins can edit
        and approve {language.name} translations, preview {language.name} in the language menu, and
        mark the language ready for you to publish. They don't get access to the rest of Admin.
      </p>
      <textarea
        className={styles.textarea}
        rows={Math.max(3, (language.admins || []).length + 1)}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setSaved(false);
        }}
        placeholder="translator@example.com"
      />
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save language admins'}
        </button>
        {saved && <span className={styles.muted}>Saved.</span>}
      </div>
    </div>
  );
};
